import { QueryClient, QueryKey } from '@tanstack/react-query'

import { useIndividualsServiceGetV1IndividualsByIndividualIdKey } from '../client/queries/common'
import { IndividualType } from './useIndividual'

const INDIVIDUAL_QUERY_KEY = [useIndividualsServiceGetV1IndividualsByIndividualIdKey]

export type IndividualSnapshots = { snapshots: [QueryKey, unknown][] }

type IndividualDocument = IndividualType['documents'][number]

/**
 * Applies `edit` to every cached individual's `documents` array and returns the
 * snapshots needed to undo it.
 *
 * Deletes go through here so the row disappears on click instead of one
 * round-trip later. In-flight fetches are cancelled first: a response that left
 * the server before the delete would otherwise land afterwards and paint the row
 * back — the exact flicker the pages' tombstone sets were added to paper over.
 */
export async function optimisticallyEditDocuments(
  queryClient: QueryClient,
  edit: (documents: IndividualDocument[]) => IndividualDocument[],
): Promise<IndividualSnapshots> {
  await queryClient.cancelQueries({ queryKey: INDIVIDUAL_QUERY_KEY })

  const snapshots = queryClient.getQueriesData({ queryKey: INDIVIDUAL_QUERY_KEY })

  queryClient.setQueriesData<IndividualType>({ queryKey: INDIVIDUAL_QUERY_KEY }, (prev) =>
    prev ? { ...prev, documents: edit(prev.documents) } : prev,
  )

  return { snapshots }
}

/** Restores the pre-mutation cache. Called when the delete actually failed. */
export function rollbackDocuments(
  queryClient: QueryClient,
  context: IndividualSnapshots | undefined,
) {
  for (const [key, data] of context?.snapshots ?? []) {
    queryClient.setQueryData(key, data)
  }
}
